import {
  enum as _enum,
  array,
  boolean,
  email,
  date,
  nullable,
  object,
  omit,
  optional,
  pipe,
  string,
  toLowerCase,
  trim,
  transform,
  union,
  type InferOutput,
} from "valibot";
import {
  AppPermissionIdentifierWithEffect,
  CognitoUserGroupPermissionDefaults,
  ValidCognitoPermissionGroupsEnum,
} from "#interface/auth";

export const InvitationRecipientEmailSchema = pipe(
  string(),
  trim(),
  toLowerCase(),
  email()
);

export const IUserDBModel = object({
  id: string(),
  email: string(),
  phone: nullable(string()),
  cognitoId: string(),
  preferredUsername: string(),
  role: _enum(ValidCognitoPermissionGroupsEnum),
  customPermissions: array(AppPermissionIdentifierWithEffect),
});
export type IUserDBModel = InferOutput<typeof IUserDBModel>;

export const IBetterAuthUserDBModel = object({
  id: string(),
  name: string(),
  firstName: string(),
  lastName: string(),
  email: string(),
  emailVerified: boolean(),
  image: nullable(string()),
  phone: nullable(string()),
  phoneVerified: boolean(),
  role: _enum(ValidCognitoPermissionGroupsEnum),
  customPermissions: array(AppPermissionIdentifierWithEffect),
  isGeneralAccount: boolean(),
  kanbanDepartments: nullable(array(string())),
  createdAt: date(),
  updatedAt: date(),
});
export type IBetterAuthUserDBModel = InferOutput<typeof IBetterAuthUserDBModel>;

export const IComboUserViewDBModel = omit(IBetterAuthUserDBModel, [
  "createdAt",
  "updatedAt",
]);
export type IComboUserViewDBModel = InferOutput<typeof IComboUserViewDBModel>;

export const IBetterAuthUserDBModelToIUserTransform = pipe(
  union([IBetterAuthUserDBModel, IComboUserViewDBModel]),
  transform((data) => {
    if (!data.role) data.role = ValidCognitoPermissionGroupsEnum.Admin;
    return {
      hasEmail: !!data.email,
      hasPhone: !!data.phone,
      id: data.id,
      email: data.email,
      emailVerified: data.emailVerified,
      phoneVerified: data.phoneVerified,
      name:
        data.firstName && data.lastName
          ? data.firstName + " " + data.lastName
          : data.name,
      firstName: data.firstName,
      lastName: data.lastName,
      role: data.role,
      isGeneralAccount: data.isGeneralAccount,
      rolePermissions: CognitoUserGroupPermissionDefaults[data.role],
      userPermissions: data.customPermissions,
      kanbanDepartments: data.kanbanDepartments,
    } satisfies IUser;
  })
);

export const IUser = object({
  id: string(),
  hasEmail: boolean(),
  hasPhone: boolean(),
  email: string(),
  // phone: optional(string()),
  emailVerified: boolean(),
  phoneVerified: boolean(),
  firstName: string(),
  lastName: string(),
  name: string(),
  role: _enum(ValidCognitoPermissionGroupsEnum),
  isGeneralAccount: optional(boolean()),
  userPermissions: array(AppPermissionIdentifierWithEffect),
  rolePermissions: array(AppPermissionIdentifierWithEffect),
  kanbanDepartments: optional(nullable(array(string()))),
});
export type IUser = InferOutput<typeof IUser>;

export const IUserDBModelToIUserTransform = pipe(
  IUserDBModel,
  transform(
    (data) =>
      ({
        id: data.id,
        hasEmail: !!data.email,
        hasPhone: !!data.phone,
        email: data.email,
        name: data.preferredUsername,
        emailVerified: true,
        phoneVerified: !!data.phone,
        firstName: data.preferredUsername.split(" ")[0] || "",
        lastName: data.preferredUsername.split(" ").slice(1).join(" ") || "",
        role: data.role,
        isGeneralAccount: false,
        rolePermissions: CognitoUserGroupPermissionDefaults[data.role],
        userPermissions: data.customPermissions,
        kanbanDepartments: null,
      } satisfies IUser)
  )
);
